// How a work order is named in a JOB PICKER — one convention, two call sites.
//
// THE PROBLEM THIS SOLVES. Both job-costing pickers labelled options by title,
// and recurring work makes titles collide by design: `createRecurringJob`
// (components/RecurringJobs.js) spawns N children that all carry the contract's
// single title, and the quote-approval path they never take is the ONLY thing
// that assigns `work_order_number` (DB trigger `auto_generate_work_order_number`
// — "Only act on approval transition"). So the disambiguator is structurally
// absent on exactly the rows that need it.
//
// Measured live 2026-09-06 on Smoke Test Plumbing (c8c96c4b…): four title groups
// of 12 / 4 / 3 / 3 rows, every one with zero work_order_number AND zero
// quote_number — and `scheduled_start` populated on all of them. A contractor
// with one weekly maintenance contract sees N identical rows and cannot tell
// which visit they are costing a $400 material purchase to.
//
// The pick itself is deterministic (value={id}), so nothing is mis-saved. The
// loss is that the person cannot tell the visits apart, and the cost lands on
// the wrong visit silently — which is worse than an error, because job costing
// is what tells them whether they made money on the work.
//
// WHY THE DATE. It is the one field populated on precisely the colliding rows,
// and it is what a contractor actually uses to tell two visits of the same
// contract apart. Rendered through fmtDate so it is the COMPANY's timezone
// (CLAUDE.md §19) — a picker that shifts a visit to the previous day for every
// tenant west of UTC would swap which of two options is which.
//
// Kept in one file because these two pages must agree: the finding pair
// (F-IRIS-2026-09-01-0134-1 on Expenses, F-ATLAS-2026-09-01-0325-1 on Purchase
// Orders) is the same bug in two label expressions, and fixing them separately
// is how they drift apart again.

import { fmtDate, DATE_NUMERIC } from './dateFormatters';

/**
 * A label that distinguishes one work order from its siblings.
 *
 * Shape: `<identifier> — <title> · <date>`, with every part optional so a row
 * missing all of them still renders something selectable rather than an empty
 * option. Falls back to `created_at` when a job is not scheduled yet, since an
 * unscheduled job still needs telling apart from its siblings.
 */
export const workOrderPickerLabel = (wo) => {
  if (!wo) return '';

  const identifier = wo.work_order_number || wo.quote_number || 'Job';
  const when = fmtDate(wo.scheduled_start || wo.created_at, DATE_NUMERIC);

  let label = identifier;
  if (wo.title) label += ` — ${wo.title}`;
  if (when) label += ` · ${when}`;

  // Only reachable if the row has no title and no date at all.
  return label || `Job ${String(wo.id || '').slice(0, 8)}`;
};

// COLUMNS THIS READS, and why they are NOT exported as a shared constant:
//
//   id, work_order_number, quote_number, title, scheduled_start, created_at
//
// `check:schema-contract` is a regex guard, not a parser, so a `.select()`
// holding a variable is invisible to it. Sharing one constant across both call
// sites would have quietly switched off phantom-column checking on exactly the
// two queries this change touches. Duplicating six column names is the cheaper
// mistake; keep them spelled out at each call site.
